import { useMemo, useState } from 'react';
import { CalendarDays } from 'lucide-react';

import { MAX_TITLE_LENGTH } from '../../constants/schedule';
import {
  formatTimezoneWithOffset,
  getAllUniqueTimezones,
} from '../../utils/date';
import type { DateRange } from '../../types/date';
import DateRangePickerPanel from './DateRangePickerPanel';
import SchedulePanelHeader from './SchedulePanelHeader';

type EventFormPanelProps = {
  isOwner: boolean;
  title?: string;
  onTitleChange: (v: string) => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  pickerRange: DateRange;
  onPickerRangeChange: (range: DateRange) => void;
  pickerStart: string;
  onPickerStartChange: (v: string) => void;
  pickerEnd: string;
  onPickerEndChange: (v: string) => void;
  onClose: () => void;
  onSave: () => void;
  className?: string;
};

const EventFormPanel = ({
  isOwner,
  title,
  onTitleChange,
  timezone,
  onTimezoneChange,
  pickerRange,
  onPickerRangeChange,
  pickerStart,
  onPickerStartChange,
  pickerEnd,
  onPickerEndChange,
  onClose,
  onSave,
  className = '',
}: EventFormPanelProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const timezoneOptions = useMemo(
    () =>
      getAllUniqueTimezones().map((tz) => ({
        value: tz,
        label: formatTimezoneWithOffset(tz, 'start'),
      })),
    [],
  );

  return (
    <div
      className={`flex flex-col flex-1 min-h-0 h-full overflow-hidden ${className}`}>
      <SchedulePanelHeader
        icon={CalendarDays}
        title="Event"
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto overscroll-y-none hide-scrollbar px-5 py-3 min-h-0">
        <div className="mb-4">
          <input
            className="w-full border rounded px-3 py-2 text-lg font-medium disabled:bg-gray-100"
            type="text"
            value={title ?? ''}
            placeholder="Event Name"
            onChange={(e) => {
              if (!isOwner) return;
              onTitleChange(e.target.value);
            }}
            disabled={!isOwner}
            maxLength={MAX_TITLE_LENGTH}
          />
        </div>
        <div className="mb-4 border rounded-xl p-3 shadow-sm">
          <DateRangePickerPanel
            isOwner={isOwner}
            pickerRange={pickerRange}
            onChange={onPickerRangeChange}
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <label className="text-base font-medium">Start Time</label>
            <select
              value={pickerStart}
              onChange={(e) => {
                if (isOwner) onPickerStartChange(e.target.value);
              }}
              disabled={!isOwner}
              className="mt-1 block w-full border rounded text-base font-medium px-2 py-1">
              {Array.from({ length: 24 }).map((_, h) => {
                const hh = String(h).padStart(2, '0');
                const val = hh + ':00';
                const label =
                  h === 0
                    ? '12 AM'
                    : h < 12
                      ? h + ' AM'
                      : h === 12
                        ? '12 PM'
                        : h - 12 + ' PM';
                return (
                  <option key={val} value={val}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-base font-medium">End Time</label>
            <select
              value={pickerEnd}
              onChange={(e) => {
                if (isOwner) onPickerEndChange(e.target.value);
              }}
              disabled={!isOwner}
              className="mt-1 block w-full border rounded text-base font-medium px-2 py-1">
              {Array.from({ length: 24 }).map((_, h) => {
                const hh = String(h).padStart(2, '0');
                const val = hh + ':00';
                const label =
                  h === 0
                    ? '12 AM'
                    : h < 12
                      ? h + ' AM'
                      : h === 12
                        ? '12 PM'
                        : h - 12 + ' PM';
                return (
                  <option key={val} value={val}>
                    {label}
                  </option>
                );
              })}
              <option value="24:00">12 AM</option>
            </select>
          </div>
        </div>
        <div className="pt-3 pb-3 border-t">
          <button
            type="button"
            className="flex w-full items-center justify-between text-base font-medium text-gray-700"
            onClick={() => setShowAdvanced((v) => !v)}>
            <span>Settings</span>
            <span className="text-base text-gray-500">
              {showAdvanced ? 'Hide' : 'Show'}
            </span>
          </button>

          {showAdvanced && (
            <div className="mt-3 space-y-2 text-sm">
              <div>
                <label className="block text-base font-semibold text-gray-700">
                  Timezone
                </label>
                <select
                  className="mt-1 block w-full rounded text-base font-medium border px-2 py-1"
                  value={timezone}
                  onChange={(e) => onTimezoneChange(e.target.value)}>
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row items-center gap-4">
          <button
            className="w-full bg-red-600 text-white py-2 rounded-lg text-base font-medium sm:text-lg hover:bg-red-700"
            onClick={onClose}>
            Cancel
          </button>
          <button
            className="w-full bg-green-700 text-white py-2 rounded-lg text-base font-medium sm:text-lg hover:bg-green-800"
            onClick={onSave}>
            Save
          </button>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Note: The default time zone is set by the owner, but each viewer can
          change the time zone for their own view.{' '}
          {timezone ? ` (${timezone})` : ''}
        </div>
      </div>
    </div>
  );
};

export default EventFormPanel;
